import { Sparkles, Globe, Users, TrendingUp, Radio } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/**
 * Global inspiration counter — D1-backed community chanting tallies.
 */
export function InspirationDashboard({ inspiration, personalCount = 0, compact = false }) {
  if (!inspiration) return null;

  const milestones = inspiration.milestones ?? [];
  const totalToday = inspiration.total_cycles_today ?? 0;
  const participations = inspiration.total_participations_today ?? 0;
  const sadhakasNow = inspiration.sadhakas_now ?? 0;
  const totalGlobal = inspiration.total_global_chants ?? totalToday;

  return (
    <section data-testid="inspiration-dashboard" className={compact ? "space-y-3" : "space-y-4 mb-8"}>
      {milestones.length > 0 && (
        <div
          className="rounded-lg border border-amber-300/60 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 flex items-start gap-2"
          data-testid="inspiration-milestone"
        >
          <Sparkles className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-amber-900">{milestones[0]}</p>
        </div>
      )}

      <div className={`grid gap-3 ${compact ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3"}`}>
        <Card className="bg-white/90 shadow-sm border-amber-100">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Radio className="w-3 h-3" /> Sādhakas now
            </p>
            <p className="text-2xl font-heading text-primary" data-testid="sadhakas-now">
              {sadhakasNow.toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white/90 shadow-sm border-amber-100">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Your repetitions
            </p>
            <p className="text-2xl font-heading text-primary" data-testid="personal-repetitions">
              {personalCount.toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white/90 shadow-sm border-amber-100">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Globe className="w-3 h-3" /> Global cycles today
            </p>
            <p className="text-2xl font-heading text-primary" data-testid="global-cycles-today">
              {totalToday.toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white/90 shadow-sm border-amber-100">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="w-3 h-3" /> Total participations today
            </p>
            <p className="text-2xl font-heading text-primary">{participations.toLocaleString()}</p>
          </CardContent>
        </Card>

        <Card className="bg-white/90 shadow-sm border-amber-100 md:col-span-2">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total global chanting count</p>
            <p className="text-2xl font-heading text-primary" data-testid="total-global-chants">
              {totalGlobal.toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {!compact && inspiration.by_chant?.length > 0 && (
        <div className="rounded-lg border bg-white/80 p-4">
          <p className="text-sm font-medium mb-3">Community chants (today)</p>
          <div className="space-y-2">
            {inspiration.by_chant.map((row) => (
              <div key={row.chant_id} className="flex items-center justify-between text-sm gap-2">
                <span className="truncate">{row.title}</span>
                <Badge variant="secondary">{row.cycles_today.toLocaleString()} cycles</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
